import { DEMO } from '@trips/shared/fixtures/demo';
import type { HomeBase, SyncRecordMap, SyncTable } from '@trips/shared/schema';
import { SYNC_TABLES } from '@trips/shared/schema-consts';
import { api, configureApi, onSignedOut } from '../lib/api';
import { isNative } from '../lib/platform';
import { db, kvGet, kvSet } from './db';
import { loadTables, setState, type Tables } from './store';
import { startSync, stopSync, wipeLocalData } from './sync';

/** Reads the local copy into memory. This is what makes every screen instant. */
export async function hydrate() {
  const rows = await db.transaction(
    'r',
    SYNC_TABLES.map((t) => db.records(t)),
    () => Promise.all(SYNC_TABLES.map((t) => db.records(t).toArray())),
  );
  const tables = Object.fromEntries(
    SYNC_TABLES.map((t, i) => [
      t,
      new Map((rows[i] as SyncRecordMap[SyncTable][]).map((r) => [r.id, r])),
    ]),
  ) as Tables;
  loadTables(tables);
  performance.mark('hydrated');
}

/** Sample data in memory only, for screenshots and design review (`?demo`). */
export function loadDemo() {
  const homeBase: HomeBase = {
    id: 'hb_demo',
    rev: 0,
    updatedAt: 0,
    createdAt: 0,
    deleted: false,
    placeId: 'plc_air_RSW',
    effectiveFrom: '1970-01-01',
  };
  const tables = Object.fromEntries(SYNC_TABLES.map((t) => [t, new Map()])) as Tables;
  for (const p of DEMO.places) tables.places.set(p.id, p);
  for (const t of DEMO.trips) tables.trips.set(t.id, t);
  for (const i of DEMO.items) tables.items.set(i.id, i);
  for (const r of DEMO.programs) tables.programs.set(r.id, r);
  for (const r of DEMO.cards) tables.cards.set(r.id, r);
  for (const r of DEMO.perks) tables.perks.set(r.id, r);
  for (const r of DEMO.redemptions) tables.redemptions.set(r.id, r);
  for (const r of DEMO.flightLive) tables.flightLive.set(r.id, r);
  for (const r of DEMO.briefs) tables.briefs.set(r.id, r);
  for (const r of DEMO.balanceSnapshots) tables.balanceSnapshots.set(r.id, r);
  tables.homeBases.set(homeBase.id, homeBase);
  loadTables(tables);
  setState({ session: 'signed-in' });
}

const DEMO_FLAG = 'trips.demo';

/** Sample mode: `?demo` in the URL, or chosen on the pairing screen before a server exists. */
export const isDemo = () => {
  if (__PREVIEW__) return true;
  if (typeof location === 'undefined') return false;
  if (new URLSearchParams(location.search).has('demo')) return true;
  try {
    return localStorage.getItem(DEMO_FLAG) === '1';
  } catch {
    return false;
  }
};

export function enterSampleMode() {
  try {
    localStorage.setItem(DEMO_FLAG, '1');
  } catch {
    // Without storage, sample mode lasts for this visit only.
  }
  loadDemo();
}

export function leaveSampleMode() {
  try {
    localStorage.removeItem(DEMO_FLAG);
  } catch {
    // Nothing stored.
  }
  location.replace('/');
}

interface DeviceAuth {
  server: string;
  token: string;
  deviceId: string;
}

/**
 * The plugin comes back wrapped in an object on purpose. A Capacitor plugin is a proxy that
 * answers to any method name, `then` included, so returning it straight from an async function
 * makes the await call `SecureStorage.then()`, which never settles. That hung the app on a
 * blank screen at start.
 */
async function secureStorage() {
  const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
  return { store: SecureStorage };
}

/** Rejects instead of hanging if the keystore never answers. */
function within<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('Timed out')), ms)),
  ]);
}

export async function loadDeviceAuth(): Promise<DeviceAuth | null> {
  if (!isNative) return null;
  const { store } = await secureStorage();
  const raw = await within(store.get('device-auth'), 5000).catch(() => null);
  if (!raw || typeof raw !== 'object') return null;
  return raw as unknown as DeviceAuth;
}

export async function saveDeviceAuth(auth: DeviceAuth) {
  const { store } = await secureStorage();
  await store.set('device-auth', auth as never);
}

interface AuthStatus {
  setupComplete: boolean;
  signedIn: boolean;
  kind: 'web' | 'device' | null;
  hasPasskey: boolean;
}

export async function bootSession() {
  if (isDemo()) return;
  onSignedOut(() => {
    stopSync();
    if (isNative) void wipeLocalData();
    else setState({ session: 'signed-out' });
    void kvSet('session.hint', false);
  });

  if (isNative) {
    // Anything going wrong here lands on the pairing screen, never a blank one.
    const auth = await loadDeviceAuth().catch(() => null);
    if (!auth) {
      setState({ session: 'needs-pairing' });
      return;
    }
    configureApi({ base: auth.server, token: auth.token });
    setState({ session: 'signed-in' });
    void startSync();
    return;
  }

  try {
    const status = await api<AuthStatus>('/api/auth/status');
    if (!status.setupComplete) setState({ session: 'needs-setup' });
    else if (status.signedIn) {
      setState({ session: 'signed-in' });
      await kvSet('session.hint', true);
      void startSync();
    } else setState({ session: 'signed-out' });
  } catch {
    // Offline: trust the last known state so the local copy stays usable.
    const hint = await kvGet<boolean>('session.hint');
    setState({ session: hint ? 'signed-in' : 'signed-out' });
    if (hint) void startSync();
  }
}

/** Called after a successful sign-in or setup. */
export async function signedIn() {
  setState({ session: 'signed-in' });
  await kvSet('session.hint', true);
  void startSync();
}

export async function signOut() {
  await api('/api/auth/logout', { body: {} }).catch(() => {});
  stopSync();
  await kvSet('session.hint', false);
  setState({ session: 'signed-out' });
}
